import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { CartItem } from '@/types';
import { trackAnalyticsEvent } from '@/lib/analytics';

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (item: CartItem) => void;
  removeItem: (index: number) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
  discountThreshold: number;
  discountPercent: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const DISCOUNT_THRESHOLD = 20;
const DISCOUNT_PERCENT = 10;
const CART_STORAGE_KEY = 'gaingrid-cart';

const loadCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen(prev => !prev), []);

  const addItem = useCallback((item: CartItem) => {
    setItems(prev => [...prev, item]);
    setIsOpen(true);
    void trackAnalyticsEvent('cart_add', {
      product: item.productTitle,
      price: item.price,
    });
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((sum, item) => sum + item.price, 0);
  const hasDiscount = subtotal >= DISCOUNT_THRESHOLD;
  const total = hasDiscount ? subtotal * (1 - DISCOUNT_PERCENT / 100) : subtotal;

  return (
    <CartContext.Provider value={{
      items,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      addItem,
      removeItem,
      clearCart,
      total,
      itemCount: items.length,
      discountThreshold: DISCOUNT_THRESHOLD,
      discountPercent: DISCOUNT_PERCENT,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
